import { PLACEHOLDER_VALUE } from '@/constants';
import type { AssayRunConfig, AssayType, InstrumentRunDataRow } from '@/types';

import { effectiveLibraryGroupCount, orderedLibraryPrepControls } from './assaySelection';

export type ControlRow = Required<
  Pick<InstrumentRunDataRow, 'name' | 'well_position' | 'demux_index' | 'flow_cell' | 'lane'>
> & {
  is_control: true;
  /** A lower_snake wire value on the catalog path, a display string on the deprecated
   *  getControlRows fallback. Rendered through `sampleTypeLabel`, which tolerates both. */
  sample_type: string;
  study_name: string;
  org_name: string;
  library_group: string;
  sample_group: string;
};

const CONTROL_SAMPLE_GROUP = 'Control';
const LP_CONTROL_2_NAME = 'Library Prep Control 2';
const LP_CONTROL_2_TYPE = 'Library Prep Control 2';
/** Well of the one control the Tau layout marks optional — the only per-run control choice today. */
const OPTIONAL_CONTROL_WELL = 'A4';

/** Carried by every control row: `SampleCreate` requires study_id XOR study_name and org_id XOR
 *  org_name on every sample, controls included — there is no controls-only study. Grouped so the
 *  two strings can't be transposed silently. */
export type RunIdentity = { orgName: string; studyName: string };

/** The study every control inherits: the first real study a customer row carries. Rows the user has
 *  not filled in still hold the grid's placeholder, which is not a study name. */
export function resolveRunStudyName(samples: ReadonlyArray<{ study_name?: string | null }>): string {
  return samples.find(s => s.study_name && s.study_name !== PLACEHOLDER_VALUE)?.study_name ?? '';
}

function makeBaseRows({ orgName, studyName }: RunIdentity): ControlRow[] {
  return [1, 2, 3, 4].flatMap(lane =>
    [
      { well_position: 'A1', demux_index: 1 },
      { well_position: 'A2', demux_index: 2 },
      { well_position: 'A3', demux_index: 3 },
    ].map(({ well_position, demux_index }) => ({
      name: 'Library Prep Control',
      flow_cell: '1',
      lane,
      well_position,
      demux_index,
      is_control: true as const,
      sample_type: 'Library Prep Control',
      study_name: studyName,
      org_name: orgName,
      library_group: '',
      sample_group: CONTROL_SAMPLE_GROUP,
    })),
  );
}

/** A4 "Library Prep Control 2" row. `populated=false` clears the data columns
 *  (Sample/Study/Org/Library Group/Sample Group) while keeping the structural
 *  Flowcell/Lane/Well/Demux so the row stays visible (POR-356, 12-sample not-accepted). */
function makeA4Row(populated: boolean, { orgName, studyName }: RunIdentity): ControlRow {
  return {
    name: populated ? LP_CONTROL_2_NAME : '',
    flow_cell: '1',
    lane: 4,
    well_position: OPTIONAL_CONTROL_WELL,
    demux_index: 2,
    is_control: true,
    sample_type: LP_CONTROL_2_TYPE,
    study_name: populated ? studyName : '',
    org_name: populated ? orgName : '',
    library_group: '',
    sample_group: populated ? CONTROL_SAMPLE_GROUP : '',
  };
}

/** Hardcoded Tau layout: A4 is opt-in at 24 samples, always blank at 12 (one group only), absent otherwise.
 *  @deprecated Fallback only, for before `/assays/run_configs` loads — see
 *  {@link getControlRowsFromAssayRunConfig}, which uses the real catalog. */
export function getControlRows(
  assayType: AssayType | undefined,
  numSamples: number,
  useA4: boolean,
  identity: RunIdentity,
): ControlRow[] {
  if (assayType !== 'tau_proteoform') return [];
  const rows = makeBaseRows(identity);
  if (numSamples === 24) {
    rows.push(makeA4Row(useA4, identity));
  } else if (numSamples === 12) {
    rows.push(makeA4Row(false, identity));
  }
  return rows;
}

/** Control rows from the selected assay's real run config. Populates exactly `libraryGroupCount`
 *  library-prep controls, mandatory first (same ordering activeLibraryGroupNames uses, so the two
 *  families of rows can never disagree), capped to one group below the assay's max sample count. */
export function getControlRowsFromAssayRunConfig(
  runConfig: AssayRunConfig | undefined,
  numSamples: number,
  libraryGroupCount: number,
  { orgName, studyName }: RunIdentity,
): ControlRow[] {
  if (!runConfig) return [];
  // `undefined` rather than Math.max(...[]) === -Infinity, which would compare true against every
  // numSamples and silently promote every optional control to required.
  const maxSampleCount =
    runConfig.valid_sample_counts.length > 0 ? Math.max(...runConfig.valid_sample_counts) : undefined;
  const atMaxSampleCount = maxSampleCount !== undefined && numSamples >= maxSampleCount;
  const count = effectiveLibraryGroupCount(runConfig, numSamples, libraryGroupCount);

  const orderedLibraryPrep = orderedLibraryPrepControls(runConfig);
  // Object-reference Set, not well_position: two library-prep controls can share a well
  // (multiplexed lanes), so a position key could wrongly populate both at once.
  const populatedLibraryPrep = new Set(orderedLibraryPrep.slice(0, count));

  return runConfig.controls.map(c => {
    const isLibraryPrep = c.control_type === 'library_prep_control';
    const populated = isLibraryPrep ? populatedLibraryPrep.has(c) : !c.is_optional || atMaxSampleCount;
    return {
      name: populated ? c.name : '',
      flow_cell: c.flow_cell,
      lane: c.lane,
      well_position: c.well_position,
      demux_index: c.demux_index,
      is_control: true as const,
      sample_type: c.control_type,
      study_name: populated ? studyName : '',
      org_name: populated ? orgName : '',
      library_group: populated ? (c.library_group ?? '') : '',
      sample_group: populated ? CONTROL_SAMPLE_GROUP : '',
    };
  });
}

/** Single entry point for control rows so the Controls table and the submit payload can never disagree. */
export function deriveControlRows(
  runConfig: AssayRunConfig | undefined,
  assayType: AssayType | undefined,
  numSamples: number,
  libraryGroupCount: number,
  identity: RunIdentity,
): ControlRow[] {
  return runConfig
    ? getControlRowsFromAssayRunConfig(runConfig, numSamples, libraryGroupCount, identity)
    : getControlRows(assayType, numSamples, libraryGroupCount >= 2, identity);
}

/** A populated row is physically on the plate, so it must be submitted; a blank-name row is a
 *  control the run declined and exists only to keep the table's shape. */
export const isPlatedControlRow = (row: ControlRow): boolean => row.name !== '';

/** Plated controls as sample-payload inputs. `org_id` comes from the run, not the org-map lookup,
 *  which returns null for an org absent from that list. A declined control is dropped and its
 *  coordinate left empty: the backend types rows by coordinate and ignores `is_optional`. */
export function controlSampleInputs(
  rows: ControlRow[],
  identity: RunIdentity,
  runOrgId: string,
): Array<{
  name: string;
  study_name: string;
  org_id: string;
  well_position: string;
  demux_index: number;
  flow_cell: string | null;
  lane: number | null;
  library_group: string | null;
}> {
  return rows.filter(isPlatedControlRow).map(c => ({
    name: c.name,
    study_name: identity.studyName,
    org_id: runOrgId,
    well_position: c.well_position,
    demux_index: c.demux_index,
    flow_cell: c.flow_cell,
    lane: c.lane,
    library_group: c.library_group,
  }));
}

/** How many library-prep-control rows a saved run actually populated, so an edit reopens with
 *  the same count. Falls back to 1 (never 0) since a run with zero controls couldn't have been
 *  submitted under the "at least one control" rule. */
export function countPopulatedLibraryPrepControls(
  samples:
    | ReadonlyArray<{ well_position?: string | null; sample_type?: string | null; name?: string | null }>
    | null
    | undefined,
): number {
  const count = (samples ?? []).filter(s => s.sample_type === 'library_prep_control' && Boolean(s.name)).length;
  return count > 0 ? count : 1;
}
